/**
 * **A new recurring series** (recurring-sessions R1–R10) — the weekdays and hours the trainer
 * picked, turned into the concrete occurrences `POST /calendarevent/recurring` is sent, one of them
 * moved by hand where it collides, and what stands in each one's way.
 *
 * The web sends EXPLICIT occurrences, never a cadence: the server creates exactly the list it is
 * given (R1), 1 to 364 of them, stationary only (R7) — and 52 weeks of all seven days is exactly 364,
 * so the week bound is the only one the dialog has to hold. A series spans 1–52 weeks counted from its
 * start day: week `w` is the seven days from `start + 7w`, and each chosen weekday occurs once in
 * each, at its own hour — so a Monday start with Tuesday and Friday chosen for 8 weeks is 16
 * sessions, the first on the Tuesday after. Every occurrence is a WALL reading on the trainer's
 * clock (`localDate` + `localTime` + the zone), so 17:00 stays 17:00 across a daylight-saving
 * change; the instant is derived here only to check it against the calendar.
 *
 * A conflict never blocks a series (R9): the server creates it and reports the overlaps. The
 * dialog names them first so the trainer can move the one or two that collide; one moved by hand
 * is keyed by the day it was generated on, so changing the hours or the weeks keeps every move
 * whose occurrence still exists and drops the rest.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type { TrainWithMeModulesTrainingDtosRequestsCreateRecurringSessionsRequest as CreateRecurringSessionsRequest } from '~/shared/api/generated/model';

import type { BookingSession, SessionKind } from './bookingSession';
import { sessionsOverlapping, type TimeSpan } from './busyTime';
import { isBookableLength, wireType, type SessionDraft } from './sessionDraft';
import {
  isoWeekday,
  paddedClock,
  shiftDay,
  wallReading,
  zonedInstant,
  type CivilDay,
} from './trainerClock';

/** How many weeks a series may span (`RecurringSessionsProcessor`). */
export const WEEK_BOUNDS = { min: 1, max: 52 } as const;

/** The weeks a new series starts with — the canon's T-A4. */
const DEFAULT_WEEKS = 8;

const MINUTE_MS = 60_000;

/** A start the trainer chose: a day and minutes past its midnight. */
export interface WallSlot {
  readonly day: CivilDay;
  readonly minutes: number;
}

export interface SeriesPlan {
  readonly clientId: number | null;
  readonly kind: SessionKind;
  readonly startDay: CivilDay;
  readonly durationMinutes: number;
  readonly weeks: number;
  /** ISO weekday (1 = Monday) → its start, in minutes past midnight. */
  readonly days: ReadonlyMap<number, number>;
  /** Occurrences moved by hand, keyed by the day each was generated on. */
  readonly moves: ReadonlyMap<CivilDay, WallSlot>;
}

export interface SeriesOccurrence {
  /** 1-based, in time order — the canon's numbered list. */
  readonly number: number;
  /** The day it was generated on — its identity while the trainer edits. */
  readonly key: CivilDay;
  readonly day: CivilDay;
  readonly minutes: number;
  readonly span: TimeSpan;
  readonly moved: boolean;
  /** What already holds part of its time, earliest first; `null` when the calendar is unread. */
  readonly conflicts: readonly BookingSession[] | null;
  /** The numbers of this series' OTHER occurrences it overlaps — two moved onto one hour. */
  readonly siblings: readonly number[];
}

/** What stops a series, and — for an occurrence already past — which one it is. */
export interface SeriesBlocker {
  readonly problem: SeriesProblem;
  readonly number: number | null;
}

/** A series that names its client — the only kind the wire takes. */
export type ClientSeries = SeriesPlan & { readonly clientId: number };

export function seriesNamesClient(plan: SeriesPlan): plan is ClientSeries {
  return plan.clientId !== null;
}

/** Why a series cannot be sent yet. */
export type SeriesProblem = 'noClient' | 'noDays' | 'midnight' | 'duration' | 'pastStart' | 'past';

/** A series started from the single session the trainer was composing: its weekday, its hour. */
export function seriesFromDraft(draft: SessionDraft): SeriesPlan {
  return {
    clientId: draft.clientId,
    kind: draft.kind,
    startDay: draft.day,
    durationMinutes: draft.durationMinutes,
    weeks: DEFAULT_WEEKS,
    days: new Map([[isoWeekday(draft.day), draft.startMinutes]]),
    moves: new Map(),
  };
}

/** The weekday added at `minutes`, or taken away when it is already in. */
export function toggleWeekday(plan: SeriesPlan, weekday: number, minutes: number): SeriesPlan {
  const days = new Map(plan.days);
  if (days.has(weekday)) days.delete(weekday);
  else days.set(weekday, minutes);
  return { ...plan, days };
}

/** One weekday's hour changed. Moves stay: a hand-moved occurrence keeps the hour it was given. */
export function retimeWeekday(plan: SeriesPlan, weekday: number, minutes: number): SeriesPlan {
  if (!plan.days.has(weekday)) return plan;
  return { ...plan, days: new Map(plan.days).set(weekday, minutes) };
}

/** The weeks, held inside 1–52. */
export function withWeeks(plan: SeriesPlan, weeks: number): SeriesPlan {
  return {
    ...plan,
    weeks: Math.min(WEEK_BOUNDS.max, Math.max(WEEK_BOUNDS.min, Math.round(weeks))),
  };
}

/** One occurrence moved to another start; moving it back to where it was generated drops the move. */
export function moveOccurrence(plan: SeriesPlan, key: CivilDay, to: WallSlot): SeriesPlan {
  const moves = new Map(plan.moves);
  const generated = plan.days.get(isoWeekday(key));
  if (to.day === key && to.minutes === generated) moves.delete(key);
  else moves.set(key, to);
  return { ...plan, moves };
}

/** The days the plan generates, in order, before any move. */
function generatedSlots(plan: SeriesPlan): WallSlot[] {
  const start = isoWeekday(plan.startDay);
  const slots: WallSlot[] = [];
  for (let week = 0; week < plan.weeks; week += 1) {
    for (const [weekday, minutes] of plan.days) {
      const offset = (weekday - start + 7) % 7;
      slots.push({ day: shiftDay(plan.startDay, week * 7 + offset), minutes });
    }
  }
  return slots.sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
}

/**
 * Every occurrence the plan makes, numbered in time order, each with what collides with it.
 * `sessions` is the trainer's calendar over the series' days, or `null` while it is unread.
 */
export function seriesOccurrences(
  plan: SeriesPlan,
  zone: string,
  sessions: readonly BookingSession[] | null,
): SeriesOccurrence[] {
  const placed = generatedSlots(plan).map((slot) => {
    const moved = plan.moves.get(slot.day);
    const at = moved ?? slot;
    const startMs = zonedInstant(at.day, at.minutes, zone);
    return {
      key: slot.day,
      day: at.day,
      minutes: at.minutes,
      span: { startMs, endMs: startMs + plan.durationMinutes * MINUTE_MS },
      moved: moved !== undefined,
    };
  });
  const ordered = placed.sort((a, b) => a.span.startMs - b.span.startMs);
  return ordered.map((occurrence, index) => ({
    ...occurrence,
    number: index + 1,
    conflicts: sessions === null ? null : sessionsOverlapping(sessions, occurrence.span),
    siblings: ordered.flatMap((other, at) =>
      at !== index && overlaps(other.span, occurrence.span) ? [at + 1] : [],
    ),
  }));
}

function overlaps(a: TimeSpan, b: TimeSpan): boolean {
  return a.startMs < b.endMs && b.startMs < a.endMs;
}

/** Whether the occurrence collides with anything — the calendar, or another of its own series. */
export function collides(occurrence: SeriesOccurrence): boolean {
  return (occurrence.conflicts ?? []).length > 0 || occurrence.siblings.length > 0;
}

/** How many of a created series' members the server reported as colliding (R9). */
export function conflictedMemberCount(
  conflicts: readonly { readonly memberEventId?: number }[] | null | undefined,
): number {
  return new Set((conflicts ?? []).map((conflict) => conflict.memberEventId)).size;
}

/** The first and last day the series touches, for reading the calendar under it. */
export function seriesWindow(plan: SeriesPlan): { first: CivilDay; last: CivilDay } {
  const days = generatedSlots(plan)
    .map((slot) => (plan.moves.get(slot.day) ?? slot).day)
    .sort();
  return { first: days[0] ?? plan.startDay, last: days[days.length - 1] ?? plan.startDay };
}

/**
 * What stops the series being sent, first reason first; `null` when it can go. A series that
 * starts before today, or one with an occurrence already behind `nowMs`, is refused here though the
 * server would create it: a series is a plan for sessions to come.
 */
export function seriesProblem(
  plan: SeriesPlan,
  occurrences: readonly SeriesOccurrence[],
  nowMs: number,
  zone: string,
): SeriesBlocker | null {
  const blocked = (problem: SeriesProblem, number: number | null = null) => ({ problem, number });
  if (plan.clientId === null) return blocked('noClient');
  if (occurrences.length === 0) return blocked('noDays');
  if (!isBookableLength(plan.durationMinutes)) return blocked('duration');
  if (occurrences.some((occurrence) => occurrence.minutes === 0)) return blocked('midnight');
  if (plan.startDay < wallReading(nowMs, zone).day) return blocked('pastStart');
  const past = occurrences.find((occurrence) => occurrence.span.startMs < nowMs);
  if (past !== undefined) return blocked('past', past.number);
  return null;
}

/**
 * The body of `POST /calendarevent/recurring`: every occurrence, explicitly, on one clock — the
 * grid's, named on every one (see `sessionDraft.createRequest`).
 */
export function seriesRequest(
  plan: ClientSeries,
  occurrences: readonly SeriesOccurrence[],
  zone: string,
): CreateRecurringSessionsRequest {
  return {
    customerId: plan.clientId,
    type: wireType(plan.kind),
    occurrences: occurrences.map((occurrence) => ({
      localDate: occurrence.day,
      localTime: paddedClock(occurrence.minutes),
      durationMinutes: plan.durationMinutes,
      zoneId: zone,
    })),
  };
}

/** A day's weekday and date as the mover's strip keys them. */
export function weekOf(day: CivilDay): CivilDay[] {
  const monday = shiftDay(day, 1 - isoWeekday(day));
  return Array.from({ length: 7 }, (_, index) => shiftDay(monday, index));
}
